import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FormRow } from '@gravity-ui/components';
import { CircleExclamationFill, PersonPlus, Xmark } from '@gravity-ui/icons';
import { Button, Icon, Modal, Select, Sheet, TextInput, Tooltip } from '@gravity-ui/uikit';
import { zodResolver } from '@hookform/resolvers/zod';

import { useAddAdminAccountMutation } from 'store/api/private/user-api/admin-api';

import {
	ADD_ACC,
	ADD_ACC_BTN,
	ADMIN,
	CANCEL,
	CHIEF,
	EMAIL,
	ENTER_EMAIL,
	ENTER_FAMILY_NAME,
	ENTER_NAME,
	ENTER_ROLE,
	ENTER_SURNAME,
	FAMILY_NAME,
	MANAGER,
	MODAL_ACC_TEXT,
	NAME,
	ROLE,
	SENT_INVITATION,
	SPECIALIST,
	SURNAME,
} from '../../constants';
import type { AccountAddForm } from '../utils/use-validation';
import { useAccountAddValidation } from '../utils/use-validation';

import styles from './styles.module.scss';

export const AccountAddModal = ({ onAdd, isAdaptive, isMobile }: any) => {
	const [open, setOpen] = useState(false);

	const [visible, setVisible] = useState(false);

	const [openRole, setOpenRole] = useState(false);

	const [userAccount, { isLoading, isError, error, isSuccess }] = useAddAdminAccountMutation();

	const { accountAddSchema } = useAccountAddValidation();

	const methods = useForm<AccountAddForm>({
		resolver: zodResolver(accountAddSchema),
		defaultValues: {
			firstName: '',
			middleName: '',
			lastName: '',
			email: '',
			role: '',
		},
		mode: 'onChange',
	});

	const {
		handleSubmit,
		register,
		setValue,
		reset,
		formState: { errors, isSubmitted, isValid },
	} = methods;

	const handleClose = () => {
		setOpen(false);
		reset();
	};

	const onSubmit = async (data: AccountAddForm) => {
		try {
			await userAccount(data).unwrap();
			onAdd();
			setOpen(false);
			reset();
			toast.success('Аккаунт специалиста успешно добавлен');
		} catch (error: any) {
			toast.error(error.data.message);
			console.log(error.data.message);
		}
	};

	const roleOptions = [
		{ value: 'ADMIN', content: ADMIN },
		{ value: 'MANAGER', content: MANAGER },
		{ value: 'CHIEF', content: CHIEF },
		{ value: 'SPECIALIST', content: SPECIALIST },
	];

	return (
		<>
			{isMobile ? (
				<>
					<Button size="l" onClick={() => setVisible(true)} view="flat">
						<Icon data={PersonPlus} size={20} />
					</Button>

					<Sheet visible={visible} onClose={() => setVisible(false)} title={ADD_ACC} className={styles.sheet}>
						<div className={styles.sheet__close}>
							<Button onClick={() => setVisible(false)} view="flat">
								<Icon data={Xmark} size={16} />
							</Button>
						</div>
						<div className={styles.modal__text}>
							<CircleExclamationFill />
							<p>{MODAL_ACC_TEXT}</p>
						</div>
						<div>
							<FormProvider {...methods}>
								<form onSubmit={handleSubmit(onSubmit)} className={styles.modal__rows}>
									<FormRow className={styles.modal__row} required label={FAMILY_NAME}>
										<TextInput
											size="xl"
											placeholder={ENTER_FAMILY_NAME}
											hasClear
											{...register('lastName', { required: true })}
											validationState={isSubmitted && errors.lastName ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={errors.lastName?.message || undefined}
										/>
									</FormRow>

									<FormRow className={styles.modal__row} required label={NAME}>
										<TextInput
											size="xl"
											placeholder={ENTER_NAME}
											hasClear
											{...register('firstName', { required: true })}
											validationState={isSubmitted && errors.firstName ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.firstName
													? 'Обязательно для заполнения'
													: undefined
											}
										/>
									</FormRow>

									<FormRow className={styles.modal__row} required label={SURNAME}>
										<TextInput
											size="xl"
											placeholder={ENTER_SURNAME}
											hasClear
											{...register('middleName', { required: true })}
											validationState={isSubmitted && errors.middleName ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.middleName
													? 'Обязательно для заполнения'
													: undefined
											}
										/>
									</FormRow>

									<FormRow className={styles.modal__row} required label={EMAIL}>
										<TextInput
											size="xl"
											placeholder={ENTER_EMAIL}
											hasClear
											{...register('email', { required: true })}
											validationState={isSubmitted && errors.email ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.email ? 'Обязательно для заполнения' : undefined
											}
										/>
									</FormRow>

									<FormRow className={styles.modal__row} required label={ROLE}>
										<Select
											open={openRole}
											onOpenChange={setOpenRole}
											hasClear
											size="xl"
											filterable
											placeholder={ENTER_ROLE}
											className={styles.modal__row__select}
											options={roleOptions}
											{...register('role', { required: true })}
											onUpdate={(value) => {
												setValue('role', value[0] || '', { shouldValidate: true });
											}}
											validationState={isSubmitted && errors.role ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.role ? 'Обязательно для заполнения' : undefined
											}
											renderPopup={({ renderList }) => (
												<Sheet
													visible={openRole}
													onClose={() => setOpenRole(false)}
													title="Выберите роль"
													className={styles.sheet}
												>
													<div className={styles.sheet__close}>
														<Button onClick={() => setOpenRole(false)} view="flat">
															<Icon data={Xmark} size={16} />
														</Button>
													</div>
													<div className={styles.sheet__content}>{renderList?.()}</div>
												</Sheet>
											)}
										/>
									</FormRow>

									<div className={styles.modal__btns}>
										<Button size="xl" view="flat-secondary" onClick={() => setVisible(false)}>
											{CANCEL}
										</Button>

										<Tooltip
											content="Заполните форму"
											disabled={isValid}
											openDelay={100}
											placement="top"
										>
											<div tabIndex={0}>
												<Button
													size="xl"
													type="submit"
													// disabled={!isValid}
													// view={isValid ? 'action' : 'raised'}
													className={styles.modal__btns__sent}
												>
													{SENT_INVITATION}
												</Button>
											</div>
										</Tooltip>
									</div>
								</form>
							</FormProvider>
						</div>
					</Sheet>
				</>
			) : (
				<>
					{isAdaptive ? (
						<Button className={styles.btn} size="l" onClick={() => setOpen(true)}>
							<Icon data={PersonPlus} size={16} />
						</Button>
					) : (
						<Button className={styles.btn} size="l" onClick={() => setOpen(true)}>
							<Icon data={PersonPlus} size={16} />
							{ADD_ACC_BTN}
						</Button>
					)}

					<Modal open={open} onClose={() => setOpen(false)} className={styles.modal}>
						<div className={styles.modal__title}>
							<h3>{ADD_ACC}</h3>

							<Xmark onClick={handleClose} cursor="pointer" />
						</div>
						<div className={styles.modal__text}>
							<CircleExclamationFill />
							<p>{MODAL_ACC_TEXT}</p>
						</div>
						<div>
							<FormProvider {...methods}>
								<form onSubmit={handleSubmit(onSubmit)} className={styles.modal__rows}>
									<FormRow className={styles.modal__row} required label={FAMILY_NAME}>
										<TextInput
											size="xl"
											placeholder={ENTER_FAMILY_NAME}
											hasClear
											{...register('lastName', { required: true })}
											validationState={isSubmitted && errors.lastName ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={errors.lastName?.message || undefined}
										/>
									</FormRow>

									<FormRow className={styles.modal__row} required label={NAME}>
										<TextInput
											size="xl"
											placeholder={ENTER_NAME}
											hasClear
											{...register('firstName', { required: true })}
											validationState={isSubmitted && errors.firstName ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.firstName
													? 'Обязательно для заполнения'
													: undefined
											}
										/>
									</FormRow>

									<FormRow className={styles.modal__row} required label={SURNAME}>
										<TextInput
											size="xl"
											placeholder={ENTER_SURNAME}
											hasClear
											{...register('middleName', { required: true })}
											validationState={isSubmitted && errors.middleName ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.middleName
													? 'Обязательно для заполнения'
													: undefined
											}
										/>
									</FormRow>

									<FormRow className={styles.modal__row} required label={EMAIL}>
										<TextInput
											size="xl"
											placeholder={ENTER_EMAIL}
											hasClear
											{...register('email', { required: true })}
											validationState={isSubmitted && errors.email ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.email ? 'Обязательно для заполнения' : undefined
											}
										/>
									</FormRow>

									<FormRow className={styles.modal__row} required label={ROLE}>
										<Select
											hasClear
											size="xl"
											filterable
											placeholder={ENTER_ROLE}
											options={roleOptions}
											className={styles.modal__row__select}
											{...register('role', { required: true })}
											onUpdate={(value) => {
												setValue('role', value[0] || '', { shouldValidate: true });
											}}
											validationState={isSubmitted && errors.role ? 'invalid' : undefined}
											errorPlacement="inside"
											errorMessage={
												isSubmitted && errors.role ? 'Обязательно для заполнения' : undefined
											}
										></Select>
									</FormRow>
									<div className={styles.modal__btns}>
										<Button size="xl" view="flat-secondary" onClick={() => setOpen(false)}>
											{CANCEL}
										</Button>

										<Tooltip
											content="Заполните форму"
											disabled={isValid}
											openDelay={100}
											placement="top"
										>
											<div tabIndex={0}>
												<Button
													size="xl"
													type="submit"
													// disabled={!isValid}
													// view={isValid ? 'action' : 'raised'}
													className={styles.modal__btns__sent}
												>
													{SENT_INVITATION}
												</Button>
											</div>
										</Tooltip>
									</div>
								</form>
							</FormProvider>
						</div>
					</Modal>
				</>
			)}
		</>
	);
};
